import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection, query, where, onSnapshot,
  updateDoc, doc, addDoc, serverTimestamp
} from 'firebase/firestore';
import {
  MapPin, CheckCircle, Plus, Navigation,
  Truck, LogOut, Phone, Home, X,
  ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';

// Interval kirim ulang lokasi ke Firestore (milisecond)
// watchPosition sudah otomatis saat GPS bergerak,
// tapi interval ini memastikan Firestore tetap update
// walau kurir diam, supaya timestamp "terakhir dilihat" tidak basi
const LOCATION_PUSH_INTERVAL = 2 * 60 * 1000; // 2 menit

export default function CourierDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [courierData, setCourierData] = useState(null);
  const [deliveries,  setDeliveries]  = useState([]);
  const [allDonors,   setAllDonors]   = useState([]);
  const [location,    setLocation]    = useState(null);  // { lat, lng, accuracy }
  const [gpsError,    setGpsError]    = useState('');
  const [sharing,     setSharing]     = useState(false);
  const [showModal,   setShowModal]   = useState(false);
  const [selectedDonor, setSelectedDonor] = useState('');
  const [note,        setNote]        = useState('');
  const [toast,       setToast]       = useState(null);
  const [expandedId,  setExpandedId]  = useState(null);

  // Refs — agar bisa diakses dari callback tanpa re-render
  const watchIdRef    = useRef(null);  // ID dari watchPosition
  const intervalRef   = useRef(null);  // ID dari setInterval
  const lastLocRef    = useRef(null);  // lokasi terakhir (untuk fallback interval)
  const courierIdRef  = useRef(null);  // Firestore doc ID kurir

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Cari dokumen kurir berdasarkan email ──────────────────
  useEffect(() => {
    if (!user?.email) return;
    const q = query(collection(db, 'couriers'), where('email', '==', user.email));
    return onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setCourierData(data);
        courierIdRef.current = data.id; // simpan ke ref agar bisa dipakai di callback GPS
      }
    });
  }, [user]);

  // ── Load pengantaran milik kurir ini ─────────────────────
  useEffect(() => {
    if (!courierData?.id) return;
    const q = query(collection(db, 'deliveries'), where('courierId', '==', courierData.id));
    return onSnapshot(q, (snap) =>
      setDeliveries(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [courierData?.id]);

  // ── Load semua donor untuk pilihan tujuan pengantaran ──────
  // Bug sebelumnya: filter by courierId sering kosong karena admin
  // tidak selalu mengisi courierId di tiap donor.
  // Solusi: load SEMUA donor, lalu filter di client:
  //   - distributionMethod = 'antar' (bukan ambil sendiri)
  //   - distributionStatus bukan 'selesai'
  useEffect(() => {
    // Load semua donors tanpa filter Firestore
    // agar tidak butuh composite index
    const q = query(collection(db, 'donors'));
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter di sisi client
      const needDelivery = all.filter(d =>
        d.distributionMethod === 'antar' &&
        d.distributionStatus !== 'selesai'
      );
      setAllDonors(needDelivery);
    });
  }, []);

  // ── Fungsi kirim lokasi ke Firestore ─────────────────────
  // Dipanggil oleh watchPosition DAN interval 2 menit
  const pushLocation = async (pos) => {
    const loc = {
      lat:       pos.coords.latitude,
      lng:       pos.coords.longitude,
      accuracy:  pos.coords.accuracy,
      timestamp: new Date().toISOString(),
    };
    setLocation(loc);
    lastLocRef.current = loc;  // simpan untuk fallback interval

    const id = courierIdRef.current;
    if (!id) return;
    try {
      await updateDoc(doc(db, 'couriers', id), {
        location: loc,
        status:   'active',
        lastSeen: serverTimestamp(),
      });
    } catch (e) {
      console.error('Gagal update lokasi:', e);
    }
  };

  // ── Mulai berbagi lokasi ──────────────────────────────────
  const startSharing = () => {
    if (!navigator.geolocation) {
      setGpsError('Browser tidak mendukung GPS');
      return;
    }

    setGpsError('');
    setSharing(true);

    // 1. watchPosition: update otomatis setiap GPS bergerak
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        pushLocation(pos);
      },
      (err) => {
        setGpsError(`GPS error: ${err.message}`);
        setSharing(false);
      },
      {
        enableHighAccuracy: true, // pakai GPS chip, bukan WiFi/cell
        maximumAge:         0,    // selalu ambil posisi fresh
        timeout:            10_000,
      }
    );

    // 2. Interval 2 menit: kirim ulang posisi terakhir
    //    Ini menjaga "lastSeen" di Firestore tetap fresh
    //    walau kurir diam (watchPosition tidak trigger jika tidak bergerak)
    intervalRef.current = setInterval(() => {
      if (lastLocRef.current) {
        const id = courierIdRef.current;
        if (id) {
          updateDoc(doc(db, 'couriers', id), {
            location: { ...lastLocRef.current, timestamp: new Date().toISOString() },
            lastSeen: serverTimestamp(),
          });
        }
      } else {
        // Belum ada lokasi → minta sekali lagi
        navigator.geolocation.getCurrentPosition(pushLocation, () => {}, { enableHighAccuracy: true });
      }
    }, LOCATION_PUSH_INTERVAL);
  };

  // ── Berhenti berbagi lokasi ───────────────────────────────
  const stopSharing = async () => {
    // Hentikan watchPosition
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    // Hentikan interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSharing(false);
    setLocation(null);
    lastLocRef.current = null;

    // Set status kurir jadi inactive di Firestore
    const id = courierIdRef.current;
    if (id) {
      await updateDoc(doc(db, 'couriers', id), { status: 'inactive' });
    }
    showToast('Berbagi lokasi dihentikan');
  };

  // ── Bersihkan saat komponen unmount ──────────────────────
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null)  navigator.geolocation.clearWatch(watchIdRef.current);
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, []);

  // ── Tambah tujuan pengantaran ─────────────────────────────
  const addDelivery = async () => {
    if (!selectedDonor || !courierData?.id) return;

    const donor = allDonors.find(d => d.id === selectedDonor);
    await addDoc(collection(db, 'deliveries'), {
      courierId:   courierData.id,
      courierName: courierData.name,
      donorId:     donor?.id     || '',
      donorName:   donor?.name   || selectedDonor,
      address:     donor?.address|| '',
      phone:       donor?.phone  || '',
      note,
      status:      'menuju',
      createdAt:   serverTimestamp(),
    });

    setShowModal(false);
    setSelectedDonor('');
    setNote('');
    showToast('Tujuan ditambahkan');
  };

  // ── Update status pengantaran ─────────────────────────────
  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'deliveries', id), {
      status,
      [`${status}At`]: serverTimestamp(),
    });
    // Jika selesai → update status donor juga
    if (status === 'selesai') {
      const delivery = deliveries.find(d => d.id === id);
      if (delivery?.donorId) {
        await updateDoc(doc(db, 'donors', delivery.donorId), {
          distributionStatus: 'selesai',
          distributionDate:   serverTimestamp(),
        });
      }
      showToast('✅ Daging berhasil diantarkan!');
    } else {
      showToast('Status diperbarui');
    }
  };

  const handleLogout = async () => {
    await stopSharing();
    await logout();
    navigate('/');
  };

  const pending = deliveries.filter(d => d.status !== 'selesai');
  const done    = deliveries.filter(d => d.status === 'selesai');

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh', background: '#f4fbf6' }}>

      {/* ── Header kurir ── */}
      <div style={{ background: '#166534', padding: '0.875rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>
            {courierData?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{courierData?.name || 'Memuat...'}</div>
            <div style={{ fontSize: 12, color: '#86efac' }}>{courierData?.vehicle || 'Motor'} · {courierData?.plateNumber || '-'}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Tombol GPS */}
          {sharing ? (
            <button onClick={stopSharing} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fca5a5', display: 'inline-block', animation: 'blink 1s infinite' }} />
              Stop Lokasi
            </button>
          ) : (
            <button onClick={startSharing} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: '#fbbf24', color: '#78350f', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Navigation size={14} /> Mulai Berbagi Lokasi
            </button>
          )}

          <button onClick={handleLogout} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.25rem 1rem 4rem' }}>

        {/* ── Status GPS ── */}
        {sharing && location && (
          <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 12, padding: '10px 16px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', display: 'inline-block', animation: 'blink 1.5s infinite', flex: 'none' }} />
            <div style={{ color: '#166534' }}>
              <strong>GPS Aktif</strong>
              {' · '}Lat: {location.lat.toFixed(5)}, Lng: {location.lng.toFixed(5)}
              <span style={{ color: '#86efac', marginLeft: 8, fontSize: 11 }}>
                Akurasi ~{Math.round(location.accuracy)}m · update tiap 2 mnt
              </span>
            </div>
          </div>
        )}

        {sharing && !location && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 16px', marginBottom: '1rem', fontSize: 13, color: '#92400e' }}>
            ⏳ Menunggu sinyal GPS... pastikan GPS aktif di pengaturan HP.
          </div>
        )}

        {gpsError && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12, padding: '10px 16px', marginBottom: '1rem', fontSize: 13, color: '#991b1b', display: 'flex', gap: 6 }}>
            <AlertCircle size={15} style={{ flex: 'none', marginTop: 1 }} /> {gpsError}
          </div>
        )}

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: '1.25rem' }}>
          {[
            { label: 'Total Tujuan', value: deliveries.length, color: '#166534' },
            { label: 'Dalam Proses', value: pending.length,    color: '#d97706' },
            { label: 'Selesai',      value: done.length,       color: '#16a34a' },
          ].map((s, i) => (
            <div key={i} className="stat-card" style={{ textAlign: 'center' }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* ── Header daftar + tombol tambah ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#14532d' }}>Daftar Pengantaran</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Tambah Tujuan
          </button>
        </div>

        {/* ── Kosong ── */}
        {deliveries.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
            <Truck size={36} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.3 }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Belum ada tujuan pengantaran</div>
            <div style={{ fontSize: 13 }}>Tambahkan tujuan atau tunggu admin assign.</div>
          </div>
        )}

        {/* ── Daftar dalam proses ── */}
        {pending.map(d => (
          <div key={d.id} className="card" style={{ marginBottom: 10, borderLeft: `4px solid ${d.status === 'tiba' ? '#0369a1' : '#d97706'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#14532d' }}>{d.donorName}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: d.status === 'tiba' ? '#0369a1' : '#d97706', background: d.status === 'tiba' ? '#dbeafe' : '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>
                    {d.status === 'tiba' ? '📍 Sudah Tiba' : '🚚 Menuju'}
                  </span>
                </div>
                {d.address && (
                  <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3 }}>
                    <Home size={11} /> {d.address}
                  </div>
                )}
                {d.phone && (
                  <a href={`tel:${d.phone}`} style={{ fontSize: 12, color: '#0369a1', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Phone size={11} /> {d.phone}
                  </a>
                )}
                {d.note && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>📝 {d.note}</div>}
              </div>
              <button onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4, flex: 'none' }}>
                {expandedId === d.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {/* Tombol aksi — muncul saat card dibuka */}
            {expandedId === d.id && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2f0e8', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {d.status === 'menuju' && (
                  <button onClick={() => updateStatus(d.id, 'tiba')}
                    style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#dbeafe', color: '#1e40af', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    📍 Sudah Tiba di Lokasi
                  </button>
                )}
                <button onClick={() => updateStatus(d.id, 'selesai')}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ✅ Daging Sudah Diterima
                </button>
                {d.address && (
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(d.address)}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e2f0e8', background: '#fff', color: '#14532d', fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <MapPin size={13} /> Buka Maps
                  </a>
                )}
              </div>
            )}
          </div>
        ))}

        {/* ── Daftar selesai ── */}
        {done.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '1.25rem 0 0.75rem' }}>
              ✅ Selesai ({done.length})
            </div>
            {done.map(d => (
              <div key={d.id} className="card" style={{ marginBottom: 8, borderLeft: '4px solid #16a34a', opacity: 0.7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#14532d' }}>{d.donorName}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{d.address || '-'}</div>
                  </div>
                  <CheckCircle size={18} color="#16a34a" />
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Modal tambah tujuan ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Tambah Tujuan Pengantaran</div>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Pilih Pengkurban</label>
              <select className="form-input form-select" value={selectedDonor} onChange={e => setSelectedDonor(e.target.value)}>
                <option value="">-- Pilih dari daftar --</option>
                {allDonors.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} — {d.address?.substring(0, 40) || 'Alamat kosong'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Catatan (opsional)</label>
              <input className="form-input" placeholder="Warna pagar, titik kumpul, dll..."
                value={note} onChange={e => setNote(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={addDelivery} disabled={!selectedDonor}>Tambahkan</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="toast-container">
          <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
        </div>
      )}

      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
    </div>
  );
}
